import { Modal } from "react-bootstrap";
import useStore from "../hooks/useStore";
import { useEffect, useRef, useState } from "react";
import { clearHubSpotState } from "../utils/hubspot";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email address is required"),
  message: Yup.string(),
  creditCardNumber: Yup.string()
    .length(16, "Credit card number must be 16 digits")
    .required("Credit card number is required"),
  expMonth: Yup.string()
    .length(2, "Expiration month must be 2 digits")
    .required("Expiration month is required"),
  expYear: Yup.string()
    .length(2, "Expiration year must be 2 digits")
    .required("Expiration year is required"),
  promotionCode: Yup.string(),
});

export default function CheckoutGiftcardModal() {
  const {
    selectedGiftCard,
    showGiftCardModal,
    setShowGiftCardModal,
    purchaseGiftCard,
    setSelectedClient,
    setSelectedGiftCard,
  } = useStore();

  const [giftCardData, setGiftCardData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    creditCardNumber: "",
    expMonth: "",
    expYear: "",
    promotionCode: "",
  });
  const [showPromoCode, setShowPromoCode] = useState(false);

  const firstNameInputRef = useRef(null);

  useEffect(() => {
    if (showGiftCardModal) firstNameInputRef.current.focus();
  }, [showGiftCardModal]);

  const handleClose = () => setShowGiftCardModal(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    setGiftCardData(values);
    try {
      setSubmitting(true);
      await purchaseGiftCard(values);
      setSubmitting(false);
      setSelectedClient(null);
      setSelectedGiftCard(null);
      clearHubSpotState();
      handleClose();
      toast.success("Purchase completed successfully");
    } catch (e) {
      toast.error(`Something went wrong! ${e?.message}`);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={showGiftCardModal}
      onHide={handleClose}
      centered
    >
      <img
        src="/swipe_your_card.png"
        style={{ position: "fixed", top: 50, right: 20 }}
      ></img>

      <Modal.Body className="p-5">
        <button
          type="button"
          className="btn-close"
          data-testid="giftcard-modal-close-btn"
          onClick={handleClose}
        ></button>
        {selectedGiftCard && (
          <div id="gift-card-details pb-4">
            <span id="gift-card-name">{selectedGiftCard.name}</span> - $
            <span id="gift-card-price">{selectedGiftCard.price}</span>
          </div>
        )}

        <h3>Check out</h3>

        <Formik
          initialValues={giftCardData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="checkout-form" autoComplete="off">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label mt-3">First Name</label>
                  <Field
                    type="text"
                    className="form-control"
                    id="firstName"
                    name="firstName"
                    aria-describedby="first-name"
                    placeholder="First Name"
                    autoComplete="off"
                    innerRef={firstNameInputRef}
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label mt-3">Last Name</label>
                  <Field
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastName"
                    aria-describedby="last-name"
                    placeholder="Last Name"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label mt-3">Email Address</label>
                  <Field
                    type="text"
                    className="form-control"
                    id="email"
                    name="email"
                    aria-describedby="email"
                    placeholder="Email Address"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label mt-3">
                    Would you like to add a message?
                  </label>
                  <Field
                    as="textarea"
                    id="message"
                    className="form-control mb-5"
                    name="message"
                    aria-label="message"
                    rows="5"
                    autoComplete="off"
                  ></Field>
                </div>

                <div className="col-md-12 py-2">
                  <Field
                    className="datasetfield w-100 form-control"
                    type="text"
                    name="creditCardNumber"
                    placeholder="Credit card number"
                    maxLength="16"
                    autoComplete="off"
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="creditCardNumber"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </div>

                <div className="col-md-12 py-2">
                  <Field
                    className="datasetfield form-control"
                    name="expMonth"
                    placeholder="03"
                    size="2"
                    maxLength="2"
                    autoComplete="off"
                    style={{ display: "inline-block", width: "auto" }}
                    disabled={isSubmitting}
                  />
                  <span style={{ display: "inline-block", padding: "0 5px" }}>
                    /
                  </span>
                  <Field
                    className="datasetfield form-control"
                    name="expYear"
                    placeholder="25"
                    maxLength="2"
                    size="2"
                    autoComplete="off"
                    style={{ display: "inline-block", width: "auto" }}
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="expMonth"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                  <ErrorMessage
                    name="expYear"
                    component="div"
                    className="invalid-feedback d-block"
                  />
                </div>

                <div className="col-md-12 py-2">
                  {!showPromoCode && (
                    <span
                      className="pointer"
                      onClick={() => setShowPromoCode(true)}
                    >
                      Enter promo code
                    </span>
                  )}
                  {showPromoCode && (
                    <Field
                      className="datasetfield w-100 form-control"
                      type=""
                      name="promotionCode"
                      id="promotionCode"
                      placeholder="Promo Code"
                      autoComplete="off"
                      autoFocus="autofocus"
                      disabled={isSubmitting}
                    />
                  )}
                </div>

                <div className="modal-footer border-0">
                  <button
                    id="pay_amount"
                    type="submit"
                    className="btn btn-primary btn-large btn-lg"
                    data-testid="giftcard-buy-btn"
                    disabled={isSubmitting}
                  >
                    Buy
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}
